import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";

import { shop, type Sku } from "@/api/shop";
import { Price } from "@/components/Price";
import { PaymentDialog } from "@/components/PaymentDialog";
import { useCart } from "@/cart/CartContext";

/** Time-limited SKUs are rented; perpetual ones are bought. */
function actionLabel(sku: Sku): string {
  return sku.duration_days ? "Rent" : "Buy";
}

export function ProductDetail() {
  const { slug = "" } = useParams();
  const { add } = useCart();
  const [added, setAdded] = useState<string | null>(null);
  const [paying, setPaying] = useState<Sku | null>(null);

  const { data: product, isLoading, error } = useQuery({
    queryKey: ["shop", "product", slug],
    queryFn: () => shop.product(slug),
    enabled: !!slug,
  });

  if (isLoading) return <p className="text-ink-soft">Loading…</p>;
  if (error || !product)
    return (
      <div>
        <p className="text-down">Product not found.</p>
        <Link to="/" className="mt-2 inline-block font-medium text-brand-strong">
          ← Back to catalog
        </Link>
      </div>
    );

  return (
    <div className="mx-auto max-w-3xl">
      <Link to="/" className="text-sm font-medium text-ink-soft hover:text-ink">
        ← Back to catalog
      </Link>

      <div className="mt-3 rounded-2xl border border-line bg-surface p-7 shadow-card">
        <span className="rounded-full bg-brand-soft px-2.5 py-1 text-[0.68rem] font-semibold uppercase tracking-wide text-brand-strong">
          {product.kind}
        </span>
        <h1 className="mt-3 text-2xl font-extrabold tracking-tight text-ink">{product.name}</h1>
        {product.summary && <p className="mt-2 text-ink-soft">{product.summary}</p>}
        {product.description_md && (
          <p className="mt-5 whitespace-pre-wrap text-sm leading-relaxed text-ink">
            {product.description_md}
          </p>
        )}
      </div>

      <h2 className="mb-3 mt-7 text-sm font-semibold uppercase tracking-wide text-ink-mute">
        Choose a plan
      </h2>
      <div className="space-y-3">
        {product.skus.map((sku) => (
          <div
            key={sku.id}
            className="flex items-center justify-between rounded-xl border border-line bg-surface p-5 shadow-card"
          >
            <div>
              <div className="font-semibold text-ink">{sku.name}</div>
              <div className="mt-0.5 text-sm text-ink-soft">
                {sku.duration_days ? `${sku.duration_days} days access` : "Lifetime access"} ·{" "}
                <span className="font-semibold text-ink">
                  <Price cents={sku.price_cents} currency={sku.currency} />
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  add({ sku_code: sku.sku_code, name: sku.name, price_cents: sku.price_cents, currency: sku.currency });
                  setAdded(sku.sku_code);
                }}
                className="rounded-lg border border-line px-3.5 py-2 text-sm font-medium text-ink-soft transition-colors hover:border-line-strong hover:text-ink"
              >
                {added === sku.sku_code ? "Added ✓" : "Add to cart"}
              </button>
              <button
                onClick={() => setPaying(sku)}
                className="rounded-lg bg-brand px-5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-strong"
              >
                {actionLabel(sku)}
              </button>
            </div>
          </div>
        ))}
        {product.skus.length === 0 && (
          <p className="text-ink-mute">This product has no purchasable options right now.</p>
        )}
      </div>

      <PaymentDialog
        open={paying != null}
        onClose={() => setPaying(null)}
        items={paying ? [{ sku_code: paying.sku_code, quantity: 1 }] : []}
        title={paying ? `${product.name} — ${paying.name}` : ""}
        totalCents={paying?.price_cents}
        currency={paying?.currency}
      />
    </div>
  );
}
